import { Row, Col, Badge } from 'react-bootstrap'
import { ICustomColumn, IDateSearchLocalStorageNames, IDealerChangedEvent, IPageProps, IRootState } from 'types'
import { DefaultButton, FormDateSpanSelector, FormDropDownList, FormSearchInput, PageHeader } from 'components'
import { V1ApiIntegrationsClient } from 'core/http-common'
import toast from 'core/toast'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { ApplicationLogDto } from 'generated/v1/v1IntegrationsApi'
import { GridCellProps, GridDetailRowProps } from '@progress/kendo-react-grid'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Field, Form, FormClassComponent, FormElement } from '@progress/kendo-react-form'
import ApplicationLogsDetails from './ApplicationLogsDetails'
import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { getFromLocalStorage, saveToLocalStorage } from 'core/local-storage-helpers'
import { IFormDateSpanSelectorRef } from 'components/Form/FormDateSpanSelector'
import DealerSelect from 'components/Dealers/DealerSelect'
import { DeletedStateEnum } from '@v1DealersApi'

// Define constants for local storage keys
const LOCAL_STORAGE_KEYS = {
    APPLICATION_NAME: 'ApplicationLogsApplicationName',
    LEVEL: 'ApplicationLogsLevel',
    DEALER: 'ApplicationLogsDealer',
    SEARCH: 'ApplicationLogsSearch',
    RELATIVE_DATE_OPTION: 'ApplicationLogsRelativeDateOption',
    END_DATE: 'ApplicationLogsEndDate',
    START_DATE: 'ApplicationLogsStartDate'
}

const minDate = moment().subtract(30, 'days').startOf('day').toDate()
const maxDate = moment().endOf('day').toDate()

const ApplicationLogsPage = ({ title }: IPageProps) => {
    const navigate = useNavigate()

    const loading = useSelector((state: IRootState) => state.app.loading)
    const [applicationNames, setApplicationNames] = useState<string[]>([])
    const [loaded, setLoaded] = useState(false)
    const gridRef = useRef<ISAGridRef>(null)
    const formDateSpanSelectorRef = useRef<IFormDateSpanSelectorRef>(null)
    const formRef = useRef<FormClassComponent>(null)
    const [selectedDealerId, setSelectedDealerId] = useState<number | undefined>(getFromLocalStorage(LOCAL_STORAGE_KEYS.DEALER) as number | undefined)

    const logLevels = [
        { id: 'All', text: 'All' },
        { id: 'Debug', text: 'Debug' },
        { id: 'Error', text: 'Error' },
        { id: 'Fatal', text: 'Fatal' },
        { id: 'Info', text: 'Info' },
        { id: 'Trace', text: 'Trace' },
        { id: 'Warn', text: 'Warn' }
    ]

    const dateSearchLocalStorageNames: IDateSearchLocalStorageNames =
    {
        relativeDateOption: LOCAL_STORAGE_KEYS.RELATIVE_DATE_OPTION,
        endDate: LOCAL_STORAGE_KEYS.END_DATE,
        startDate: LOCAL_STORAGE_KEYS.START_DATE
    }

    // Define the type for initialValues to allow dealer to be IDropDownValue | undefined
    interface InitialValuesType {
        applicationName: string;
        level: { id: string; text: string } | undefined;
        search: string | undefined;
    }

    const defaultValues: InitialValuesType = {
        applicationName: 'All',
        level: logLevels.find(x => x.id === 'All'),
        search: undefined
    }

    const [initialValues, setInitialValues] = useState<InitialValuesType>({ ...defaultValues })

    useEffect(() => {
        setInitialValues({
            applicationName: getFromLocalStorage(LOCAL_STORAGE_KEYS.APPLICATION_NAME) ?? 'All',
            level: getFromLocalStorage(LOCAL_STORAGE_KEYS.LEVEL) ?? logLevels.find(x => x.id === 'All'),
            search: getFromLocalStorage(LOCAL_STORAGE_KEYS.SEARCH)
        })
        setLoaded(true)
    }, [])

    const fetch = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        let values = formRef.current?.values
        let page = props.paging && props.paging.currentPage ? props.paging.currentPage : 1

        try {
            const result = await V1ApiIntegrationsClient.getApplicationLogs(
                page,
                props.paging ? props.paging.take : 10,
                page === 1,
                props.sortBy,
                props.sortAscending,
                values?.startDate ? values.startDate : undefined,
                values?.endDate ? values.endDate : undefined,
                values?.search,
                selectedDealerId,
                values?.applicationName && values.applicationName !== 'All'
                    ? values.applicationName
                    : undefined,
                values?.level && values.level.id !== 'All'
                    ? values.level.id
                    : undefined
            )
            if (result.totals && result.totals.applicationNames)
                setApplicationNames(result.totals.applicationNames)

            // On page 1, we get totalItems from API. On page 2+, totalItems is null/undefined, so use existing total
            const totalCount = result.totalItems != null ? result.totalItems : (props.paging?.total ?? 0)

            return {
                data: result.data,
                total: totalCount,
                page: page,
                pageSize: props.paging ? props.paging.take : 10,
                skip: props.paging ? props.paging.skip : 0
            }
        }
        catch (e) {
            toast.responseErrors(e)
            return {
                data: [],
                total: 0
            }
        }
    }

    const GoToApplicationLogDetails = (props: GridCellProps) => {
        let item = props.dataItem as ApplicationLogDto

        return (
            <td
                className='text-center cursor-pointer'
                title='Go To Application Log Details Page'
                onClick={() => navigate(`/logs/application-logs/${item.indexName}/${item.id}`)}>
                <a
                    className='btn btn-default btn-sm gridButton'
                    role='button'
                >
                    <FontAwesomeIcon icon={['fas', 'up-right-from-square']} />
                </a>
            </td>
        )
    }

    const ExceptionMessageCell = (props: GridCellProps) => {
        const item = props.dataItem as ApplicationLogDto
        let displayMessage: string | undefined

        if (item.message) {
            try {
                JSON.parse(item.message)
            } catch {
                displayMessage = item.message
            }
        }

        if (!displayMessage)
            displayMessage = item.exceptionMessage || item.additionalInfo || undefined

        return (
            <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={displayMessage}>
                {displayMessage}
            </td>
        )
    }

    const getBadgeVariant = (level: string) => {
        switch (level.toLowerCase()) {
            case 'error':
                return 'danger'
            case 'warn':
                return 'warning'
            case 'info':
                return 'info'
            case 'debug':
                return 'secondary'
            default:
                return 'primary'
        }
    }

    const LogLevelCell = (props: GridCellProps) => {
        const log = props.dataItem as ApplicationLogDto
        return (
            <td>
                {log.level ?
                    <Badge bg={getBadgeVariant(log.level)}>{log.level}</Badge>
                    : null
                }
            </td>
        )
    }

    const handleDealerClear = async () => {
        setSelectedDealerId(undefined)
        saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, undefined)
    }

    const handleDealerChange = async (e: IDealerChangedEvent) => {
        setSelectedDealerId(e.dealerId)
        saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, e.dealerId)
    }

    useEffect(() => {
        gridRef.current?.resetAndFetch()
    }, [selectedDealerId])

    const columns: ICustomColumn[] = [
        { field: 'dealerId', title: 'Dealer ID', width: '150px' },
        { field: 'dealerName', title: 'Dealer Name', sortable: false, width: '150px' },
        { field: 'userName', title: 'User Name', sortable: false, width: '150px' },
        { field: 'machineName', title: 'Machine', sortable: false, width: '150px' },
        { field: 'applicationName', title: 'Application', sortable: false, width: '140px' },
        { field: 'level', title: 'Level', sortable: false, width: '100px', cell: LogLevelCell },
        { field: 'exceptionMessage', title: 'Message', sortable: false, cell: ExceptionMessageCell },
        { field: 'timestamp', title: 'Timestamp', filter: 'date', format: '{0:G}', width: '180px' },
        { field: '', title: 'Actions', cell: GoToApplicationLogDetails, sortable: false, width: '80px' }
    ]

    const DetailComponent = (props: GridDetailRowProps) => (<ApplicationLogsDetails log={props.dataItem as ApplicationLogDto} />)

    const searchByDate = async (start: Date, end: Date) => {
        formRef.current?.valueSetter('startDate', start)
        formRef.current?.valueSetter('endDate', end)
        gridRef.current?.resetAndFetch()
    }

    if (!loaded)
        return null

    return (
        <div id='ApplicationLogsPage'>
            <PageHeader title={title} />

            <Form
                ref={formRef}
                ignoreModified={true}
                initialValues={initialValues}
                key={JSON.stringify(initialValues)}
                render={(formRenderProps) => (
                    <FormElement>
                        <div className='bg-light border rounded p-3 shadow-sm mb-3'>
                            <Row className='mb-3'>
                                <Col xs={12}>
                                    <FormDateSpanSelector
                                        ref={formDateSpanSelectorRef}
                                        className=''
                                        formRenderProps={formRenderProps}
                                        localStorageNames={dateSearchLocalStorageNames}
                                        onSearch={searchByDate}
                                        maxDate={maxDate}
                                        minDate={minDate}
                                        disabled={loading}
                                    />
                                </Col>
                                <Col lg={4}>
                                    <Row>
                                        <Col xs={9}>
                                            <Field
                                                label='Application:'
                                                name='applicationName'
                                                component={FormDropDownList}
                                                data={applicationNames}
                                                disabled={loading}
                                                onChange={() => {
                                                    gridRef.current?.resetAndFetch()
                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.APPLICATION_NAME, formRenderProps.valueGetter('applicationName'))
                                                }}
                                            />
                                        </Col>
                                        <Col xs={3} style={{ alignSelf: 'end' }}>
                                            <Field
                                                label='Level:'
                                                name='level'
                                                component={FormDropDownList}
                                                textField='text'
                                                dataItemKey='id'
                                                data={logLevels}
                                                disabled={loading}
                                                onChange={() => {
                                                    gridRef.current?.resetAndFetch()
                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.LEVEL, formRenderProps.valueGetter('level'))
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col lg={4}>
                                    <Field
                                        name='dealer'
                                        label='Dealer:'
                                        component={DealerSelect}
                                        dealerId={selectedDealerId ?? 0}
                                        disabled={loading}
                                        onDealerChanged={handleDealerChange}
                                        onDealerCleared={handleDealerClear}
                                        required={false}
                                        defaultDeletedState={DeletedStateEnum.Both}
                                    />
                                </Col>

                                <Col lg={4}>
                                    <Row>
                                        <Col xs={8}>
                                            <Field
                                                component={FormSearchInput}
                                                name='search'
                                                label='Search:'
                                                disabled={loading}
                                                onSearch={() => {
                                                    gridRef.current?.resetAndFetch()
                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.SEARCH, formRenderProps.valueGetter('search'))
                                                }}
                                            />
                                        </Col>

                                        <Col xs={4} style={{ alignSelf: 'end' }}>
                                            <DefaultButton className='w-100' variant='default'
                                                onClick={() => {
                                                    setInitialValues({ ...defaultValues })
                                                    setSelectedDealerId(undefined)
                                                    formRef.current?.resetForm()
                                                    formDateSpanSelectorRef.current?.reset()
                                                    gridRef.current?.resetAndFetch()

                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.APPLICATION_NAME, defaultValues.applicationName)
                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.LEVEL, defaultValues.level)
                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.DEALER, undefined)
                                                    saveToLocalStorage(LOCAL_STORAGE_KEYS.SEARCH, defaultValues.search)
                                                }}
                                                disabled={loading}>
                                                Clear All
                                            </DefaultButton>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </div>
                    </FormElement>

                )}
            />
            <Row>
                <Col>
                    <SAGrid
                        ref={gridRef}
                        features={
                            {
                                pageable: true,
                                sortable: true,
                                filterable: false,
                                exportToExcel: false,
                                exportToPDF: false
                            }
                        }
                        columns={columns}
                        defaultSort={[{ field: 'timestamp', dir: 'desc' }]}
                        detail={DetailComponent}
                        getData={fetch}
                    />
                </Col>
            </Row>
        </div>
    )
}

export default ApplicationLogsPage